import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardBody, Container, Nav, NavItem, NavLink, Button, Label, Input, Row, Col } from "reactstrap";
import classnames from "classnames";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { invalidateParametresQueries } from "../../../hooks/queries/useRoadAlertQueries";
import {
  getNiveauGraviteByCriteria,
  createNiveauGravite,
  updateNiveauGravite,
  deleteNiveauGravite,
  getTypeDegradationByCriteria,
  createTypeDegradation,
  updateTypeDegradation,
  deleteTypeDegradation,
  getFamilleDegradationByCriteria,
  createFamilleDegradation,
  updateFamilleDegradation,
  deleteFamilleDegradation,
  getDegradationByCriteria,
  createDegradation,
  updateDegradation,
  deleteDegradation,
  getParametreByCriteria,
  createParametre,
  updateParametre,
  deleteParametre,
  getTypeChausseByCriteria,
  createTypeChausse,
  updateTypeChausse,
  deleteTypeChausse,
  getRegionsByCriteria,
  createRegion,
  updateRegion,
  deleteRegion,
  getDepartementsByCriteria,
  createDepartement,
  updateDepartement,
  deleteDepartement,
  getTypeAlerteByCriteria,
  createTypeAlerte,
  updateTypeAlerte,
  deleteTypeAlerte,
} from "../../../services/request-api";
import { DataTable, Column, TableAction } from "../../../Components/Common/DataTable";
import { showConfirmModal, showModal, hideModal } from "../../../hooks/useModal";
import { toastSuccess, toastError } from "../../../utils/toast";
import { useAuthStore } from "../../../stores/useAuthStore";
import {
  fakeGravites,
  fakeTypeDegradations,
  fakeEnsDegradations,
  fakeDegradationsList,
  fakeParametres,
  fakeTypeChausse,
  fakeTypeAlerte,
  fakeRegions,
  fakeDepartements,
} from "../../../utils/fakeData";

const TABS = [
  { id: "gravite", label: "Gravité", fetch: getNiveauGraviteByCriteria },
  { id: "typeDegradation", label: "Type Dégradation", fetch: getTypeDegradationByCriteria },
  { id: "ensDegradation", label: "Ens Dégradation", fetch: getFamilleDegradationByCriteria },
  { id: "degradation", label: "Dégradation", fetch: getDegradationByCriteria },
  { id: "parametre", label: "Paramètres", fetch: getParametreByCriteria },
  { id: "typeChausse", label: "Type chaussé", fetch: getTypeChausseByCriteria },
  { id: "typeAlerte", label: "Type alerte", fetch: getTypeAlerteByCriteria },
  { id: "region", label: "Région", fetch: getRegionsByCriteria },
  { id: "departement", label: "Département", fetch: getDepartementsByCriteria },
];

/** Contenu de la modal création / édition gravité */
function GraviteFormModal({
  initialRow,
  onSuccess,
}: {
  initialRow: { idNiveauGravite?: number; libelleNiveauGravite?: string; notation?: string } | null;
  onSuccess: () => void;
}) {
  const isEdit = initialRow != null && initialRow.idNiveauGravite != null;
  const [libelle, setLibelle] = useState(initialRow?.libelleNiveauGravite ?? "");
  const [notation, setNotation] = useState(initialRow?.notation ?? "");

  const handleSubmit = () => {
    if (!libelle.trim()) {
      toastError("Le niveau gravité est obligatoire.");
      return;
    }
    showConfirmModal({
      title: isEdit ? "Confirmer la modification" : "Confirmer la création",
      message: isEdit
        ? "Voulez-vous enregistrer les modifications de cette gravité ?"
        : "Voulez-vous créer cette gravité ?",
      confirmText: "Oui, enregistrer",
      cancelText: "Annuler",
      confirmVariant: "primary",
      type: "info",
      onConfirm: () => {
        const payload = isEdit
          ? updateNiveauGravite({
              data: {
                idNiveauGravite: initialRow!.idNiveauGravite!,
                libelleNiveauGravite: libelle.trim(),
                notation: notation.trim(),
              },
            })
          : createNiveauGravite({
              data: { libelleNiveauGravite: libelle.trim(), notation: notation.trim() },
            });
        payload
          .then(() => {
            toastSuccess(isEdit ? "Gravité modifiée." : "Gravité créée.");
            hideModal();
            onSuccess();
          })
          .catch((err: any) => {
            toastError(err?.message ?? (isEdit ? "Erreur lors de la modification." : "Erreur lors de la création."));
          });
      },
    });
  };

  return (
    <div className="p-3">
      <Row className="g-3">
        <Col md={12}>
          <Label>Niveau gravité</Label>
          <Input
            value={libelle}
            onChange={(e) => setLibelle(e.target.value)}
            placeholder="Ex: Gravité 1"
          />
        </Col>
        <Col md={12}>
          <Label>Notation</Label>
          <Input
            value={notation}
            onChange={(e) => setNotation(e.target.value)}
            placeholder="Ex: Gravité faible"
          />
        </Col>
      </Row>
      <div className="mt-3 d-flex justify-content-end gap-2">
        <Button color="secondary" outline onClick={() => hideModal()}>
          Annuler
        </Button>
        <Button color="primary" onClick={handleSubmit}>
          Enregistrer
        </Button>
      </div>
    </div>
  );
}

/** Contenu de la modal création / édition type de dégradation */
function TypeDegradationFormModal({
  initialRow,
  onSuccess,
}: {
  initialRow: { idTypeDegradation?: number; libelleTypeDegradation?: string } | null;
  onSuccess: () => void;
}) {
  const isEdit = initialRow != null && initialRow.idTypeDegradation != null;
  const [libelle, setLibelle] = useState(initialRow?.libelleTypeDegradation ?? "");

  const handleSubmit = () => {
    if (!libelle.trim()) {
      toastError("Le libellé est obligatoire.");
      return;
    }
    showConfirmModal({
      title: isEdit ? "Confirmer la modification" : "Confirmer la création",
      message: isEdit
        ? "Voulez-vous enregistrer les modifications de ce type de dégradation ?"
        : "Voulez-vous créer ce type de dégradation ?",
      confirmText: "Oui, enregistrer",
      cancelText: "Annuler",
      confirmVariant: "primary",
      type: "info",
      onConfirm: () => {
        const payload = isEdit
          ? updateTypeDegradation({
              data: {
                idTypeDegradation: initialRow!.idTypeDegradation!,
                libelleTypeDegradation: libelle.trim(),
              },
            })
          : createTypeDegradation({
              data: { libelleTypeDegradation: libelle.trim() },
            });
        payload
          .then(() => {
            toastSuccess(isEdit ? "Type de dégradation modifié." : "Type de dégradation créé.");
            hideModal();
            onSuccess();
          })
          .catch((err: any) => {
            toastError(err?.message ?? (isEdit ? "Erreur lors de la modification." : "Erreur lors de la création."));
          });
      },
    });
  };

  return (
    <div className="p-3">
      <Row className="g-3">
        <Col md={12}>
          <Label>Libellé</Label>
          <Input
            value={libelle}
            onChange={(e) => setLibelle(e.target.value)}
            placeholder="Ex: Dégradation non structurelle"
          />
        </Col>
      </Row>
      <div className="mt-3 d-flex justify-content-end gap-2">
        <Button color="secondary" outline onClick={() => hideModal()}>
          Annuler
        </Button>
        <Button color="primary" onClick={handleSubmit}>
          Enregistrer
        </Button>
      </div>
    </div>
  );
}

/** Contenu de la modal création / édition ens. dégradation */
function EnsDegradationFormModal({
  initialRow,
  onSuccess,
}: {
  initialRow: { idEnsDegradation?: number; libelleEnsDegradation?: string; idTypeDegradation?: string | number } | null;
  onSuccess: () => void;
}) {
  const isEdit = initialRow != null && initialRow.idEnsDegradation != null;
  const [libelle, setLibelle] = useState(initialRow?.libelleEnsDegradation ?? "");
  const [idTypeDegradation, setIdTypeDegradation] = useState<string>(String(initialRow?.idTypeDegradation ?? ""));
  const [typeOptions, setTypeOptions] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    getTypeDegradationByCriteria({ data: {} })
      .then((res: any) => {
        let list: any[] = [];
        if (Array.isArray(res)) list = res;
        else if (res?.items && Array.isArray(res.items)) list = res.items;
        else if (res?.content && Array.isArray(res.content)) list = res.content;
        else if (res?.data && Array.isArray(res.data)) list = res.data;
        setTypeOptions(
          list.map((t: any) => ({
            value: String(t.idTypeDegradation ?? t.id ?? ""),
            label: t.libelleTypeDegradation ?? t.libelle ?? "-",
          }))
        );
      })
      .catch(() => setTypeOptions([]));
  }, []);

  const handleSubmit = () => {
    if (!libelle.trim()) {
      toastError("Le libellé est obligatoire.");
      return;
    }
    if (!idTypeDegradation.trim()) {
      toastError("Le type de dégradation est obligatoire.");
      return;
    }
    showConfirmModal({
      title: isEdit ? "Confirmer la modification" : "Confirmer la création",
      message: isEdit
        ? "Voulez-vous enregistrer les modifications de cet ens. dégradation ?"
        : "Voulez-vous créer cet ens. dégradation ?",
      confirmText: "Oui, enregistrer",
      cancelText: "Annuler",
      confirmVariant: "primary",
      type: "info",
      onConfirm: () => {
        const payload = isEdit
          ? updateFamilleDegradation({
              data: {
                idEnsDegradation: initialRow!.idEnsDegradation!,
                libelleEnsDegradation: libelle.trim(),
                idTypeDegradation: idTypeDegradation.trim(),
              },
            })
          : createFamilleDegradation({
              data: { libelleEnsDegradation: libelle.trim(), idTypeDegradation: idTypeDegradation.trim() },
            });
        payload
          .then(() => {
            toastSuccess(isEdit ? "Ens. dégradation modifié." : "Ens. dégradation créé.");
            hideModal();
            onSuccess();
          })
          .catch((err: any) => {
            toastError(err?.message ?? (isEdit ? "Erreur lors de la modification." : "Erreur lors de la création."));
          });
      },
    });
  };

  return (
    <div className="p-3">
      <Row className="g-3">
        <Col md={12}>
          <Label>Libellé</Label>
          <Input
            value={libelle}
            onChange={(e) => setLibelle(e.target.value)}
            placeholder="Ex: Végétation"
          />
        </Col>
        <Col md={12}>
          <Label>Type de dégradation</Label>
          <Input
            type="select"
            value={idTypeDegradation}
            onChange={(e) => setIdTypeDegradation(e.target.value)}
          >
            <option value="">Sélectionner un type</option>
            {typeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Input>
        </Col>
      </Row>
      <div className="mt-3 d-flex justify-content-end gap-2">
        <Button color="secondary" outline onClick={() => hideModal()}>
          Annuler
        </Button>
        <Button color="primary" onClick={handleSubmit}>
          Enregistrer
        </Button>
      </div>
    </div>
  );
}

/** Contenu de la modal création / édition dégradation */
function DegradationFormModal({
  initialRow,
  onSuccess,
}: {
  initialRow: { idDegradation?: number; libelleDegradation?: string; idEnsDegradation?: string | number } | null;
  onSuccess: () => void;
}) {
  const isEdit = initialRow != null && initialRow.idDegradation != null;
  const [libelle, setLibelle] = useState(initialRow?.libelleDegradation ?? "");
  const [idEnsDegradation, setIdEnsDegradation] = useState<string>(String(initialRow?.idEnsDegradation ?? ""));
  const [ensOptions, setEnsOptions] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    getFamilleDegradationByCriteria({ data: {} })
      .then((res: any) => {
        let list: any[] = [];
        if (Array.isArray(res)) list = res;
        else if (res?.items && Array.isArray(res.items)) list = res.items;
        else if (res?.content && Array.isArray(res.content)) list = res.content;
        else if (res?.data && Array.isArray(res.data)) list = res.data;
        setEnsOptions(
          list.map((e: any) => ({
            value: String(e.idEnsDegradation ?? e.id ?? ""),
            label: e.libelleEnsDegradation ?? e.libelle ?? "-",
          }))
        );
      })
      .catch(() => setEnsOptions([]));
  }, []);

  const handleSubmit = () => {
    if (!libelle.trim()) {
      toastError("Le libellé est obligatoire.");
      return;
    }
    if (!idEnsDegradation.trim()) {
      toastError("L'ens. dégradation est obligatoire.");
      return;
    }
    showConfirmModal({
      title: isEdit ? "Confirmer la modification" : "Confirmer la création",
      message: isEdit
        ? "Voulez-vous enregistrer les modifications de cette dégradation ?"
        : "Voulez-vous créer cette dégradation ?",
      confirmText: "Oui, enregistrer",
      cancelText: "Annuler",
      confirmVariant: "primary",
      type: "info",
      onConfirm: () => {
        const payload = isEdit
          ? updateDegradation({
              data: {
                idDegradation: initialRow!.idDegradation!,
                libelleDegradation: libelle.trim(),
                idEnsDegradation: idEnsDegradation.trim(),
              },
            })
          : createDegradation({
              data: { libelleDegradation: libelle.trim(), idEnsDegradation: idEnsDegradation.trim() },
            });
        payload
          .then(() => {
            toastSuccess(isEdit ? "Dégradation modifiée." : "Dégradation créée.");
            hideModal();
            onSuccess();
          })
          .catch((err: any) => {
            toastError(err?.message ?? (isEdit ? "Erreur lors de la modification." : "Erreur lors de la création."));
          });
      },
    });
  };

  return (
    <div className="p-3">
      <Row className="g-3">
        <Col md={12}>
          <Label>Libellé</Label>
          <Input
            value={libelle}
            onChange={(e) => setLibelle(e.target.value)}
            placeholder="Ex: Fissure d'adaptation"
          />
        </Col>
        <Col md={12}>
          <Label>Ens. dégradation</Label>
          <Input
            type="select"
            value={idEnsDegradation}
            onChange={(e) => setIdEnsDegradation(e.target.value)}
          >
            <option value="">Sélectionner un ens. dégradation</option>
            {ensOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Input>
        </Col>
      </Row>
      <div className="mt-3 d-flex justify-content-end gap-2">
        <Button color="secondary" outline onClick={() => hideModal()}>
          Annuler
        </Button>
        <Button color="primary" onClick={handleSubmit}>
          Enregistrer
        </Button>
      </div>
    </div>
  );
}

/** Contenu de la modal création / édition paramètre */
function ParametreFormModal({
  initialRow,
  onSuccess,
}: {
  initialRow: { idParametre?: number; libelleParametre?: string; valeur?: string } | null;
  onSuccess: () => void;
}) {
  const isEdit = initialRow != null && initialRow.idParametre != null;
  const [libelle, setLibelle] = useState(initialRow?.libelleParametre ?? "");
  const [valeur, setValeur] = useState(initialRow?.valeur ?? "");

  const handleSubmit = () => {
    if (!libelle.trim()) {
      toastError("Le libellé est obligatoire.");
      return;
    }
    showConfirmModal({
      title: isEdit ? "Confirmer la modification" : "Confirmer la création",
      message: isEdit
        ? "Voulez-vous enregistrer les modifications de ce paramètre ?"
        : "Voulez-vous créer ce paramètre ?",
      confirmText: "Oui, enregistrer",
      cancelText: "Annuler",
      confirmVariant: "primary",
      type: "info",
      onConfirm: () => {
        const payload = isEdit
          ? updateParametre({
              data: {
                idParametre: initialRow!.idParametre!,
                libelleParametre: libelle.trim(),
                valeur: valeur.trim(),
              },
            })
          : createParametre({
              data: { libelleParametre: libelle.trim(), valeur: valeur.trim() },
            });
        payload
          .then(() => {
            toastSuccess(isEdit ? "Paramètre modifié." : "Paramètre créé.");
            hideModal();
            onSuccess();
          })
          .catch((err: any) => {
            toastError(err?.message ?? (isEdit ? "Erreur lors de la modification." : "Erreur lors de la création."));
          });
      },
    });
  };

  return (
    <div className="p-3">
      <Row className="g-3">
        <Col md={12}>
          <Label>Libellé</Label>
          <Input
            value={libelle}
            onChange={(e) => setLibelle(e.target.value)}
            placeholder="Ex: Forme"
          />
        </Col>
        <Col md={12}>
          <Label>Valeur</Label>
          <Input
            value={valeur}
            onChange={(e) => setValeur(e.target.value)}
            placeholder="Ex: Isolée/En chapelet/En continu"
          />
        </Col>
      </Row>
      <div className="mt-3 d-flex justify-content-end gap-2">
        <Button color="secondary" outline onClick={() => hideModal()}>
          Annuler
        </Button>
        <Button color="primary" onClick={handleSubmit}>
          Enregistrer
        </Button>
      </div>
    </div>
  );
}

/** Contenu de la modal création / édition type chaussé */
function TypeChausseFormModal({
  initialRow,
  onSuccess,
}: {
  initialRow: { idTypeChausse?: number; libelleTypeChausse?: string } | null;
  onSuccess: () => void;
}) {
  const isEdit = initialRow != null && initialRow.idTypeChausse != null;
  const [libelle, setLibelle] = useState(initialRow?.libelleTypeChausse ?? "");

  const handleSubmit = () => {
    if (!libelle.trim()) {
      toastError("Le libellé est obligatoire.");
      return;
    }
    showConfirmModal({
      title: isEdit ? "Confirmer la modification" : "Confirmer la création",
      message: isEdit
        ? "Voulez-vous enregistrer les modifications de ce type chaussé ?"
        : "Voulez-vous créer ce type chaussé ?",
      confirmText: "Oui, enregistrer",
      cancelText: "Annuler",
      confirmVariant: "primary",
      type: "info",
      onConfirm: () => {
        const payload = isEdit
          ? updateTypeChausse({
              data: {
                idTypeChausse: initialRow!.idTypeChausse!,
                libelleTypeChausse: libelle.trim(),
              },
            })
          : createTypeChausse({
              data: { libelleTypeChausse: libelle.trim() },
            });
        payload
          .then(() => {
            toastSuccess(isEdit ? "Type chaussé modifié." : "Type chaussé créé.");
            hideModal();
            onSuccess();
          })
          .catch((err: any) => {
            toastError(err?.message ?? (isEdit ? "Erreur lors de la modification." : "Erreur lors de la création."));
          });
      },
    });
  };

  return (
    <div className="p-3">
      <Row className="g-3">
        <Col md={12}>
          <Label>Libellé</Label>
          <Input
            value={libelle}
            onChange={(e) => setLibelle(e.target.value)}
            placeholder="Ex: Enrobé bitumineux"
          />
        </Col>
      </Row>
      <div className="mt-3 d-flex justify-content-end gap-2">
        <Button color="secondary" outline onClick={() => hideModal()}>
          Annuler
        </Button>
        <Button color="primary" onClick={handleSubmit}>
          Enregistrer
        </Button>
      </div>
    </div>
  );
}

/** Contenu de la modal création / édition type alerte */
function TypeAlerteFormModal({
  initialRow,
  onSuccess,
}: {
  initialRow: { idTypeAlerte?: number; libTypeAlerte?: string } | null;
  onSuccess: () => void;
}) {
  const isEdit = initialRow != null && initialRow.idTypeAlerte != null;
  const [libelle, setLibelle] = useState(initialRow?.libTypeAlerte ?? "");

  const handleSubmit = () => {
    if (!libelle.trim()) {
      toastError("Le libellé est obligatoire.");
      return;
    }
    showConfirmModal({
      title: isEdit ? "Confirmer la modification" : "Confirmer la création",
      message: isEdit
        ? "Voulez-vous enregistrer les modifications de ce type d'alerte ?"
        : "Voulez-vous créer ce type d'alerte ?",
      confirmText: "Oui, enregistrer",
      cancelText: "Annuler",
      confirmVariant: "primary",
      type: "info",
      onConfirm: () => {
        const payload = isEdit
          ? updateTypeAlerte({
              data: {
                idTypeAlerte: initialRow!.idTypeAlerte!,
                libTypeAlerte: libelle.trim(),
              },
            })
          : createTypeAlerte({
              data: { libTypeAlerte: libelle.trim() },
            });
        payload
          .then(() => {
            toastSuccess(isEdit ? "Type alerte modifié." : "Type alerte créé.");
            hideModal();
            onSuccess();
          })
          .catch((err: any) => {
            toastError(err?.message ?? (isEdit ? "Erreur lors de la modification." : "Erreur lors de la création."));
          });
      },
    });
  };

  return (
    <div className="p-3">
      <Row className="g-3">
        <Col md={12}>
          <Label>Libellé</Label>
          <Input
            value={libelle}
            onChange={(e) => setLibelle(e.target.value)}
            placeholder="Ex: Routière, Grand public"
          />
        </Col>
      </Row>
      <div className="mt-3 d-flex justify-content-end gap-2">
        <Button color="secondary" outline onClick={() => hideModal()}>
          Annuler
        </Button>
        <Button color="primary" onClick={handleSubmit}>
          Enregistrer
        </Button>
      </div>
    </div>
  );
}

/** Contenu de la modal création / édition région */
function RegionFormModal({
  initialRow,
  onSuccess,
}: {
  initialRow: { idRegion?: number; libelleRegion?: string } | null;
  onSuccess: () => void;
}) {
  const isEdit = initialRow != null && initialRow.idRegion != null;
  const [libelle, setLibelle] = useState(initialRow?.libelleRegion ?? "");

  const handleSubmit = () => {
    if (!libelle.trim()) {
      toastError("Le libellé est obligatoire.");
      return;
    }
    showConfirmModal({
      title: isEdit ? "Confirmer la modification" : "Confirmer la création",
      message: isEdit
        ? "Voulez-vous enregistrer les modifications de cette région ?"
        : "Voulez-vous créer cette région ?",
      confirmText: "Oui, enregistrer",
      cancelText: "Annuler",
      confirmVariant: "primary",
      type: "info",
      onConfirm: () => {
        const payload = isEdit
          ? updateRegion({
              data: {
                idRegion: initialRow!.idRegion!,
                libelleRegion: libelle.trim(),
              },
            })
          : createRegion({
              data: { libelleRegion: libelle.trim() },
            });
        payload
          .then(() => {
            toastSuccess(isEdit ? "Région modifiée." : "Région créée.");
            hideModal();
            onSuccess();
          })
          .catch((err: any) => {
            toastError(err?.message ?? (isEdit ? "Erreur lors de la modification." : "Erreur lors de la création."));
          });
      },
    });
  };

  return (
    <div className="p-3">
      <Row className="g-3">
        <Col md={12}>
          <Label>Libellé</Label>
          <Input
            value={libelle}
            onChange={(e) => setLibelle(e.target.value)}
            placeholder="Ex: Région du Bagoé"
          />
        </Col>
      </Row>
      <div className="mt-3 d-flex justify-content-end gap-2">
        <Button color="secondary" outline onClick={() => hideModal()}>
          Annuler
        </Button>
        <Button color="primary" onClick={handleSubmit}>
          Enregistrer
        </Button>
      </div>
    </div>
  );
}

/** Contenu de la modal création / édition département */
function DepartementFormModal({
  initialRow,
  onSuccess,
}: {
  initialRow: { idDepartement?: number; libelleDepartement?: string; idRegion?: string | number } | null;
  onSuccess: () => void;
}) {
  const isEdit = initialRow != null && initialRow.idDepartement != null;
  const [libelle, setLibelle] = useState(initialRow?.libelleDepartement ?? "");
  const [idRegion, setIdRegion] = useState<string>(String(initialRow?.idRegion ?? ""));
  const [regionOptions, setRegionOptions] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    getRegionsByCriteria({ user: 1, data: {} })
      .then((res: any) => {
        let list: any[] = [];
        if (Array.isArray(res)) list = res;
        else if (res?.items && Array.isArray(res.items)) list = res.items;
        else if (res?.content && Array.isArray(res.content)) list = res.content;
        else if (res?.data && Array.isArray(res.data)) list = res.data;
        setRegionOptions(
          list.map((r: any) => ({
            value: String(r.idRegion ?? r.id ?? ""),
            label: r.libelleRegion ?? r.libelle ?? "-",
          }))
        );
      })
      .catch(() => setRegionOptions([]));
  }, []);

  const handleSubmit = () => {
    if (!libelle.trim()) {
      toastError("Le libellé est obligatoire.");
      return;
    }
    if (!idRegion.trim()) {
      toastError("La région est obligatoire.");
      return;
    }
    showConfirmModal({
      title: isEdit ? "Confirmer la modification" : "Confirmer la création",
      message: isEdit
        ? "Voulez-vous enregistrer les modifications de ce département ?"
        : "Voulez-vous créer ce département ?",
      confirmText: "Oui, enregistrer",
      cancelText: "Annuler",
      confirmVariant: "primary",
      type: "info",
      onConfirm: () => {
        const payload = isEdit
          ? updateDepartement({
              data: {
                idDepartement: initialRow!.idDepartement!,
                libelleDepartement: libelle.trim(),
                idRegion: idRegion.trim(),
              },
            })
          : createDepartement({
              data: { libelleDepartement: libelle.trim(), idRegion: idRegion.trim() },
            });
        payload
          .then(() => {
            toastSuccess(isEdit ? "Département modifié." : "Département créé.");
            hideModal();
            onSuccess();
          })
          .catch((err: any) => {
            toastError(err?.message ?? (isEdit ? "Erreur lors de la modification." : "Erreur lors de la création."));
          });
      },
    });
  };

  return (
    <div className="p-3">
      <Row className="g-3">
        <Col md={12}>
          <Label>Libellé</Label>
          <Input
            value={libelle}
            onChange={(e) => setLibelle(e.target.value)}
            placeholder="Ex: Tengréla"
          />
        </Col>
        <Col md={12}>
          <Label>Région</Label>
          <Input
            type="select"
            value={idRegion}
            onChange={(e) => setIdRegion(e.target.value)}
          >
            <option value="">Sélectionner une région</option>
            {regionOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Input>
        </Col>
      </Row>
      <div className="mt-3 d-flex justify-content-end gap-2">
        <Button color="secondary" outline onClick={() => hideModal()}>
          Annuler
        </Button>
        <Button color="primary" onClick={handleSubmit}>
          Enregistrer
        </Button>
      </div>
    </div>
  );
}

const Parametres = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("gravite");
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const user = useAuthStore((s) => s.user);
  /** Page et actions réservées à ROLE_ADMINISTRATEUR. */
  const canAddDeleteParametres = Boolean(
    user?.roles && Array.isArray(user.roles) && user.roles.includes("ROLE_ADMINISTRATEUR")
  );

  const currentTab = TABS.find((t) => t.id === activeTab);

  const loadData = useCallback(() => {
    if (!currentTab) return;
    setLoading(true);
    const params = activeTab === "region" ? { user: 1, data: {} } : { data: {} };
    currentTab
      .fetch(params)
      .then((res: any) => {
        let apiData: any[] = [];
        if (Array.isArray(res)) apiData = res;
        else if (res?.items && Array.isArray(res.items)) apiData = res.items;
        else if (res?.content && Array.isArray(res.content)) apiData = res.content;
        else if (res?.data && Array.isArray(res.data)) apiData = res.data;
        const fallbackData: any[] = {
          gravite: fakeGravites,
          typeDegradation: fakeTypeDegradations,
          ensDegradation: fakeEnsDegradations,
          degradation: fakeDegradationsList,
          parametre: fakeParametres,
          typeChausse: fakeTypeChausse,
          typeAlerte: fakeTypeAlerte,
          region: fakeRegions,
          departement: fakeDepartements,
        }[activeTab] ?? [];
        setData(apiData.length > 0 ? apiData : fallbackData);
      })
      .catch(() => {
        const fallbackData: any[] = {
          gravite: fakeGravites,
          typeDegradation: fakeTypeDegradations,
          ensDegradation: fakeEnsDegradations,
          degradation: fakeDegradationsList,
          parametre: fakeParametres,
          typeChausse: fakeTypeChausse,
          typeAlerte: fakeTypeAlerte,
          region: fakeRegions,
          departement: fakeDepartements,
        }[activeTab] ?? [];
        setData(fallbackData);
      })
      .finally(() => setLoading(false));
  }, [activeTab, currentTab]);

  /** Après création / modification / suppression : invalidation des queries + rechargement local. */
  const handleParametresSuccess = useCallback(() => {
    invalidateParametresQueries(queryClient);
    loadData();
  }, [queryClient, loadData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Configuration des colonnes selon l'onglet actif
  const columns: Column[] = useMemo(() => {
    switch (activeTab) {
      case "gravite":
        return [
          {
            key: "idNiveauGravite",
            label: "ID",
            sortable: true,
            visible: true,
            render: (value: number) => (
              <span className="text-gray-600">{value ? `#${value}` : "-"}</span>
            ),
          },
          {
            key: "libelleNiveauGravite",
            label: "Niveau gravité",
            sortable: true,
            visible: true,
            render: (_: unknown, row: any) => (
              <span className="fw-semibold text-gray-900">
                {row.libelleNiveauGravite || row.libelle || "-"}
              </span>
            ),
          },
          {
            key: "notation",
            label: "Notation",
            sortable: true,
            visible: true,
            render: (_: unknown, row: any) => (
              <span className="text-gray-700">{row.notation || "-"}</span>
            ),
          },
        ];

      case "typeDegradation":
        return [
          {
            key: "idTypeDegradation",
            label: "ID",
            sortable: true,
            visible: true,
            render: (value: number) => (
              <span className="text-gray-600">{value != null ? `#${value}` : "-"}</span>
            ),
          },
          {
            key: "libelleTypeDegradation",
            label: "Libellé",
            sortable: true,
            visible: true,
            render: (_: unknown, row: any) => (
              <span className="text-gray-900">{row.libelleTypeDegradation || row.libelle || "-"}</span>
            ),
          },
        ];

      case "ensDegradation":
        return [
          {
            key: "idEnsDegradation",
            label: "ID",
            sortable: true,
            visible: true,
            render: (value: number) => (
              <span className="text-gray-600">{value != null ? `#${value}` : "-"}</span>
            ),
          },
          {
            key: "libelleEnsDegradation",
            label: "Libellé",
            sortable: true,
            visible: true,
            render: (_: unknown, row: any) => (
              <span className="text-gray-900">{row.libelleEnsDegradation || row.libelle || "-"}</span>
            ),
          },
          {
            key: "typeDegradation",
            label: "Type de dégradation",
            sortable: true,
            visible: true,
            render: (_: unknown, row: any) => (
              <span className="text-gray-700">
                {row.typeDegradation?.libelleTypeDegradation ?? row.libelleTypeDegradation ?? "-"}
              </span>
            ),
          },
        ];

      case "degradation":
        return [
          {
            key: "idDegradation",
            label: "ID",
            sortable: true,
            visible: true,
            render: (value: number) => (
              <span className="text-gray-600">{value != null ? `#${value}` : "-"}</span>
            ),
          },
          {
            key: "libelleDegradation",
            label: "Libellé",
            sortable: true,
            visible: true,
            render: (_: unknown, row: any) => (
              <span className="text-gray-900">{row.libelleDegradation || row.libelle || "-"}</span>
            ),
          },
          {
            key: "ensDegradation",
            label: "Ens dégradation",
            sortable: true,
            visible: true,
            render: (_: unknown, row: any) => (
              <span className="text-gray-700">
                {row.ensDegradation?.libelleEnsDegradation ?? "-"}
              </span>
            ),
          },
        ];

      case "parametre":
        return [
          {
            key: "idParametre",
            label: "ID",
            sortable: true,
            visible: true,
            render: (value: number) => (
              <span className="text-gray-600">{value != null ? `#${value}` : "-"}</span>
            ),
          },
          {
            key: "libelleParametre",
            label: "Libellé",
            sortable: true,
            visible: true,
            render: (_: unknown, row: any) => (
              <span className="fw-semibold text-gray-900">
                {row.libelleParametre || row.libelle || "-"}
              </span>
            ),
          },
          {
            key: "valeur",
            label: "Valeur",
            sortable: true,
            visible: true,
            render: (value: string) => (
              <span className="text-gray-700">{value || "-"}</span>
            ),
          },
        ];

      case "typeChausse":
        return [
          {
            key: "idTypeChausse",
            label: "ID",
            sortable: true,
            visible: true,
            render: (value: number) => (
              <span className="text-gray-600">{value != null ? `#${value}` : "-"}</span>
            ),
          },
          {
            key: "libelleTypeChausse",
            label: "Libellé",
            sortable: true,
            visible: true,
            render: (_: unknown, row: any) => (
              <span className="text-gray-900">{row.libelleTypeChausse || row.libelle || "-"}</span>
            ),
          },
        ];

      case "typeAlerte":
        return [
          {
            key: "idTypeAlerte",
            label: "ID",
            sortable: true,
            visible: true,
            render: (value: number) => (
              <span className="text-gray-600">{value != null ? `#${value}` : "-"}</span>
            ),
          },
          {
            key: "libTypeAlerte",
            label: "Libellé",
            sortable: true,
            visible: true,
            render: (_: unknown, row: any) => (
              <span className="text-gray-900">{row.libTypeAlerte || row.libelle || "-"}</span>
            ),
          },
        ];

      case "region":
        return [
          {
            key: "idRegion",
            label: "ID",
            sortable: true,
            visible: true,
            render: (value: number) => (
              <span className="text-gray-600">{value != null ? `#${value}` : "-"}</span>
            ),
          },
          {
            key: "libelleRegion",
            label: "Libellé",
            sortable: true,
            visible: true,
            render: (_: unknown, row: any) => (
              <span className="text-gray-900">{row.libelleRegion || row.libelle || "-"}</span>
            ),
          },
        ];

      case "departement":
        return [
          {
            key: "idDepartement",
            label: "ID",
            sortable: true,
            visible: true,
            render: (value: number) => (
              <span className="text-gray-600">{value != null ? `#${value}` : "-"}</span>
            ),
          },
          {
            key: "libelleDepartement",
            label: "Libellé",
            sortable: true,
            visible: true,
            render: (_: unknown, row: any) => (
              <span className="fw-semibold text-gray-900">
                {row.libelleDepartement || row.libelle || "-"}
              </span>
            ),
          },
          {
            key: "region",
            label: "Région",
            sortable: true,
            visible: true,
            render: (_: unknown, row: any) => (
              <span className="text-gray-700">
                {row.region?.libelleRegion ?? "-"}
              </span>
            ),
          },
        ];

      default:
        return [];
    }
  }, [activeTab]);

  // Actions selon l'onglet (Gravité, Type Dégradation)
  const actions: TableAction<any>[] | undefined = useMemo(() => {
    if (activeTab === "gravite") {
      return [
        {
          label: "Modifier",
          icon: "ri-edit-line",
          onClick: (row) => {
            showModal({
              title: "Modifier la gravité",
              size: "md",
              content: (
                <GraviteFormModal
                  initialRow={{
                    idNiveauGravite: row.idNiveauGravite,
                    libelleNiveauGravite: row.libelleNiveauGravite ?? row.libelle,
                    notation: row.notation,
                  }}
                  onSuccess={handleParametresSuccess}
                />
              ),
              haveDefaultActionsButton: false,
              closeOnBackdropClick: true,
            });
          },
          variant: "info",
        },
        {
          label: "Supprimer",
          icon: "ri-delete-bin-line",
          onClick: (row) => {
            const libelle = row.libelleNiveauGravite || row.libelle || "cette gravité";
            showConfirmModal({
              title: "Supprimer la gravité",
              message: `Êtes-vous sûr de vouloir supprimer « ${libelle} » ? Cette action est irréversible.`,
              confirmText: "Oui, supprimer",
              cancelText: "Annuler",
              confirmVariant: "danger",
              type: "delete",
              onConfirm: () => {
                const id = Number(row.idNiveauGravite);
                if (Number.isNaN(id)) {
                  toastError("ID gravité invalide.");
                  return;
                }
                deleteNiveauGravite({ data: { idNiveauGravite: id } })
                  .then(() => {
                    toastSuccess("Gravité supprimée.");
                    handleParametresSuccess();
                  })
                  .catch((err: any) => {
                    toastError(err?.message ?? "Erreur lors de la suppression.");
                  });
              },
            });
          },
          variant: "danger",
        },
      ];
    }

    if (activeTab === "typeDegradation") {
      return [
        {
          label: "Modifier",
          icon: "ri-edit-line",
          onClick: (row) => {
            showModal({
              title: "Modifier le type de dégradation",
              size: "md",
              content: (
                <TypeDegradationFormModal
                  initialRow={{
                    idTypeDegradation: row.idTypeDegradation,
                    libelleTypeDegradation: row.libelleTypeDegradation ?? row.libelle,
                  }}
                  onSuccess={handleParametresSuccess}
                />
              ),
              haveDefaultActionsButton: false,
              closeOnBackdropClick: true,
            });
          },
          variant: "info",
        },
        {
          label: "Supprimer",
          icon: "ri-delete-bin-line",
          onClick: (row) => {
            const libelle = row.libelleTypeDegradation || row.libelle || "ce type de dégradation";
            showConfirmModal({
              title: "Supprimer le type de dégradation",
              message: `Êtes-vous sûr de vouloir supprimer « ${libelle} » ? Cette action est irréversible.`,
              confirmText: "Oui, supprimer",
              cancelText: "Annuler",
              confirmVariant: "danger",
              type: "delete",
              onConfirm: () => {
                const idRaw =
                  row.idTypeDegradation ??
                  row.id ??
                  row.id_type_degradation ??
                  (row.typeDegradation && typeof row.typeDegradation === "object" ? row.typeDegradation.idTypeDegradation ?? row.typeDegradation.id : undefined);
                const id = idRaw != null && idRaw !== "" ? Number(idRaw) : NaN;
                if (Number.isNaN(id)) {
                  toastError("ID invalide ou manquant.");
                  return;
                }
                deleteTypeDegradation({ data: { idTypeDegradation: String(id) } })
                  .then(() => {
                    toastSuccess("Type de dégradation supprimé.");
                    handleParametresSuccess();
                  })
                  .catch((err: any) => {
                    toastError(err?.message ?? "Erreur lors de la suppression.");
                  });
              },
            });
          },
          variant: "danger",
        },
      ];
    }

    if (activeTab === "ensDegradation") {
      return [
        {
          label: "Modifier",
          icon: "ri-edit-line",
          onClick: (row) => {
            const idType = row.idTypeDegradation ?? row.typeDegradation?.idTypeDegradation ?? row.typeDegradation?.id ?? row.id_type_degradation ?? "";
            showModal({
              title: "Modifier l'ens. dégradation",
              size: "md",
              content: (
                <EnsDegradationFormModal
                  initialRow={{
                    idEnsDegradation: row.idEnsDegradation ?? row.id,
                    libelleEnsDegradation: row.libelleEnsDegradation ?? row.libelle,
                    idTypeDegradation: idType,
                  }}
                  onSuccess={handleParametresSuccess}
                />
              ),
              haveDefaultActionsButton: false,
              closeOnBackdropClick: true,
            });
          },
          variant: "info",
        },
        {
          label: "Supprimer",
          icon: "ri-delete-bin-line",
          onClick: (row) => {
            const libelle = row.libelleEnsDegradation || row.libelle || "cet ens. dégradation";
            showConfirmModal({
              title: "Supprimer l'ens. dégradation",
              message: `Êtes-vous sûr de vouloir supprimer « ${libelle} » ? Cette action est irréversible.`,
              confirmText: "Oui, supprimer",
              cancelText: "Annuler",
              confirmVariant: "danger",
              type: "delete",
              onConfirm: () => {
                const idRaw = row.idEnsDegradation ?? row.id ?? row.id_ens_degradation;
                const id = idRaw != null && idRaw !== "" ? Number(idRaw) : NaN;
                if (Number.isNaN(id)) {
                  toastError("ID invalide ou manquant.");
                  return;
                }
                deleteFamilleDegradation({ data: { idEnsDegradation: id } })
                  .then(() => {
                    toastSuccess("Ens. dégradation supprimé.");
                    handleParametresSuccess();
                  })
                  .catch((err: any) => {
                    toastError(err?.message ?? "Erreur lors de la suppression.");
                  });
              },
            });
          },
          variant: "danger",
        },
      ];
    }

    if (activeTab === "degradation") {
      return [
        {
          label: "Modifier",
          icon: "ri-edit-line",
          onClick: (row) => {
            const idEns = row.idEnsDegradation ?? row.ensDegradation?.idEnsDegradation ?? row.ensDegradation?.id ?? "";
            showModal({
              title: "Modifier la dégradation",
              size: "md",
              content: (
                <DegradationFormModal
                  initialRow={{
                    idDegradation: row.idDegradation ?? row.id,
                    libelleDegradation: row.libelleDegradation ?? row.libelle,
                    idEnsDegradation: idEns,
                  }}
                  onSuccess={handleParametresSuccess}
                />
              ),
              haveDefaultActionsButton: false,
              closeOnBackdropClick: true,
            });
          },
          variant: "info",
        },
        {
          label: "Supprimer",
          icon: "ri-delete-bin-line",
          onClick: (row) => {
            const libelle = row.libelleDegradation || row.libelle || "cette dégradation";
            showConfirmModal({
              title: "Supprimer la dégradation",
              message: `Êtes-vous sûr de vouloir supprimer « ${libelle} » ? Cette action est irréversible.`,
              confirmText: "Oui, supprimer",
              cancelText: "Annuler",
              confirmVariant: "danger",
              type: "delete",
              onConfirm: () => {
                const idRaw = row.idDegradation ?? row.id ?? row.id_degradation;
                const id = idRaw != null && idRaw !== "" ? Number(idRaw) : NaN;
                if (Number.isNaN(id)) {
                  toastError("ID invalide ou manquant.");
                  return;
                }
                deleteDegradation({ data: { idDegradation: id } })
                  .then(() => {
                    toastSuccess("Dégradation supprimée.");
                    handleParametresSuccess();
                  })
                  .catch((err: any) => {
                    toastError(err?.message ?? "Erreur lors de la suppression.");
                  });
              },
            });
          },
          variant: "danger",
        },
      ];
    }

    if (activeTab === "parametre") {
      return [
        {
          label: "Modifier",
          icon: "ri-edit-line",
          onClick: (row) => {
            showModal({
              title: "Modifier le paramètre",
              size: "md",
              content: (
                <ParametreFormModal
                  initialRow={{
                    idParametre: row.idParametre ?? row.id,
                    libelleParametre: row.libelleParametre ?? row.libelle,
                    valeur: row.valeur ?? "",
                  }}
                  onSuccess={handleParametresSuccess}
                />
              ),
              haveDefaultActionsButton: false,
              closeOnBackdropClick: true,
            });
          },
          variant: "info",
        },
        {
          label: "Supprimer",
          icon: "ri-delete-bin-line",
          onClick: (row) => {
            const libelle = row.libelleParametre || row.libelle || "ce paramètre";
            showConfirmModal({
              title: "Supprimer le paramètre",
              message: `Êtes-vous sûr de vouloir supprimer « ${libelle} » ? Cette action est irréversible.`,
              confirmText: "Oui, supprimer",
              cancelText: "Annuler",
              confirmVariant: "danger",
              type: "delete",
              onConfirm: () => {
                const idRaw = row.idParametre ?? row.id ?? row.id_parametre;
                const id = idRaw != null && idRaw !== "" ? Number(idRaw) : NaN;
                if (Number.isNaN(id)) {
                  toastError("ID invalide ou manquant.");
                  return;
                }
                deleteParametre({ data: { idParametre: id } })
                  .then(() => {
                    toastSuccess("Paramètre supprimé.");
                    handleParametresSuccess();
                  })
                  .catch((err: any) => {
                    toastError(err?.message ?? "Erreur lors de la suppression.");
                  });
              },
            });
          },
          variant: "danger",
        },
      ];
    }

    if (activeTab === "typeChausse") {
      return [
        {
          label: "Modifier",
          icon: "ri-edit-line",
          onClick: (row) => {
            showModal({
              title: "Modifier le type chaussé",
              size: "md",
              content: (
                <TypeChausseFormModal
                  initialRow={{
                    idTypeChausse: row.idTypeChausse ?? row.id ?? row.id_type_chausse,
                    libelleTypeChausse: row.libelleTypeChausse ?? row.libelle ?? row.libelle_type_chausse,
                  }}
                  onSuccess={handleParametresSuccess}
                />
              ),
              haveDefaultActionsButton: false,
              closeOnBackdropClick: true,
            });
          },
          variant: "info",
        },
        {
          label: "Supprimer",
          icon: "ri-delete-bin-line",
          onClick: (row) => {
            const libelle = row.libelleTypeChausse ?? row.libelle ?? "ce type chaussé";
            showConfirmModal({
              title: "Supprimer le type chaussé",
              message: `Êtes-vous sûr de vouloir supprimer « ${libelle} » ? Cette action est irréversible.`,
              confirmText: "Oui, supprimer",
              cancelText: "Annuler",
              confirmVariant: "danger",
              type: "delete",
              onConfirm: () => {
                const idRaw = row.idTypeChausse ?? row.id ?? row.id_type_chausse;
                const id = idRaw != null && idRaw !== "" ? Number(idRaw) : NaN;
                if (Number.isNaN(id)) {
                  toastError("ID invalide ou manquant.");
                  return;
                }
                deleteTypeChausse({ data: { idTypeChausse: id } })
                  .then(() => {
                    toastSuccess("Type chaussé supprimé.");
                    handleParametresSuccess();
                  })
                  .catch((err: any) => {
                    toastError(err?.message ?? "Erreur lors de la suppression.");
                  });
              },
            });
          },
          variant: "danger",
        },
      ];
    }

    if (activeTab === "typeAlerte") {
      return [
        {
          label: "Modifier",
          icon: "ri-edit-line",
          onClick: (row) => {
            showModal({
              title: "Modifier le type d'alerte",
              size: "md",
              content: (
                <TypeAlerteFormModal
                  initialRow={{
                    idTypeAlerte: row.idTypeAlerte ?? row.id ?? row.id_type_alerte,
                    libTypeAlerte: row.libTypeAlerte ?? row.libelle ?? row.libelle_type_alerte,
                  }}
                  onSuccess={handleParametresSuccess}
                />
              ),
              haveDefaultActionsButton: false,
              closeOnBackdropClick: true,
            });
          },
          variant: "info",
        },
        {
          label: "Supprimer",
          icon: "ri-delete-bin-line",
          onClick: (row) => {
            const libelle = row.libTypeAlerte ?? row.libelle ?? "ce type d'alerte";
            showConfirmModal({
              title: "Supprimer le type d'alerte",
              message: `Êtes-vous sûr de vouloir supprimer « ${libelle} » ? Cette action est irréversible.`,
              confirmText: "Oui, supprimer",
              cancelText: "Annuler",
              confirmVariant: "danger",
              type: "delete",
              onConfirm: () => {
                const idRaw = row.idTypeAlerte ?? row.id ?? row.id_type_alerte;
                const id = idRaw != null && idRaw !== "" ? Number(idRaw) : NaN;
                if (Number.isNaN(id)) {
                  toastError("ID invalide ou manquant.");
                  return;
                }
                deleteTypeAlerte({ data: { idTypeAlerte: id } })
                  .then(() => {
                    toastSuccess("Type alerte supprimé.");
                    handleParametresSuccess();
                  })
                  .catch((err: any) => {
                    toastError(err?.message ?? "Erreur lors de la suppression.");
                  });
              },
            });
          },
          variant: "danger",
        },
      ];
    }

    if (activeTab === "region") {
      return [
        {
          label: "Modifier",
          icon: "ri-edit-line",
          onClick: (row) => {
            showModal({
              title: "Modifier la région",
              size: "md",
              content: (
                <RegionFormModal
                  initialRow={{
                    idRegion: row.idRegion ?? row.id ?? row.id_region,
                    libelleRegion: row.libelleRegion ?? row.libelle ?? row.libelle_region,
                  }}
                  onSuccess={handleParametresSuccess}
                />
              ),
              haveDefaultActionsButton: false,
              closeOnBackdropClick: true,
            });
          },
          variant: "info",
        },
        {
          label: "Supprimer",
          icon: "ri-delete-bin-line",
          onClick: (row) => {
            const libelle = row.libelleRegion ?? row.libelle ?? "cette région";
            showConfirmModal({
              title: "Supprimer la région",
              message: `Êtes-vous sûr de vouloir supprimer « ${libelle} » ? Cette action est irréversible.`,
              confirmText: "Oui, supprimer",
              cancelText: "Annuler",
              confirmVariant: "danger",
              type: "delete",
              onConfirm: () => {
                const idRaw = row.idRegion ?? row.id ?? row.id_region;
                const id = idRaw != null && idRaw !== "" ? Number(idRaw) : NaN;
                if (Number.isNaN(id)) {
                  toastError("ID invalide ou manquant.");
                  return;
                }
                deleteRegion({ data: { idRegion: id } })
                  .then(() => {
                    toastSuccess("Région supprimée.");
                    handleParametresSuccess();
                  })
                  .catch((err: any) => {
                    toastError(err?.message ?? "Erreur lors de la suppression.");
                  });
              },
            });
          },
          variant: "danger",
        },
      ];
    }

    if (activeTab === "departement") {
      return [
        {
          label: "Modifier",
          icon: "ri-edit-line",
          onClick: (row) => {
            const idRegionVal = row.idRegion ?? row.region?.idRegion ?? row.id_region ?? "";
            showModal({
              title: "Modifier le département",
              size: "md",
              content: (
                <DepartementFormModal
                  initialRow={{
                    idDepartement: row.idDepartement ?? row.id ?? row.id_departement,
                    libelleDepartement: row.libelleDepartement ?? row.libelle ?? row.libelle_departement,
                    idRegion: idRegionVal,
                  }}
                  onSuccess={handleParametresSuccess}
                />
              ),
              haveDefaultActionsButton: false,
              closeOnBackdropClick: true,
            });
          },
          variant: "info",
        },
        {
          label: "Supprimer",
          icon: "ri-delete-bin-line",
          onClick: (row) => {
            const libelle = row.libelleDepartement ?? row.libelle ?? "ce département";
            showConfirmModal({
              title: "Supprimer le département",
              message: `Êtes-vous sûr de vouloir supprimer « ${libelle} » ? Cette action est irréversible.`,
              confirmText: "Oui, supprimer",
              cancelText: "Annuler",
              confirmVariant: "danger",
              type: "delete",
              onConfirm: () => {
                const idRaw = row.idDepartement ?? row.id ?? row.id_departement;
                const id = idRaw != null && idRaw !== "" ? Number(idRaw) : NaN;
                if (Number.isNaN(id)) {
                  toastError("ID invalide ou manquant.");
                  return;
                }
                deleteDepartement({ data: { idDepartement: id } })
                  .then(() => {
                    toastSuccess("Département supprimé.");
                    handleParametresSuccess();
                  })
                  .catch((err: any) => {
                    toastError(err?.message ?? "Erreur lors de la suppression.");
                  });
              },
            });
          },
          variant: "danger",
        },
      ];
    }

    return undefined;
  }, [activeTab, handleParametresSuccess]);

  /** Masque l'action Supprimer pour les rôles qui ne peuvent pas (ex. ROLE_AGENTTERRAIN). */
  const filteredActions = useMemo(
    () =>
      actions && (canAddDeleteParametres ? actions : actions.filter((a) => a.label !== "Supprimer")),
    [actions, canAddDeleteParametres]
  );

  document.title = "Paramètres | Road Alert AGEROUTE";

  if (user != null && !canAddDeleteParametres) {
    return <Navigate to="/dashboard-alertes" replace />;
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Paramètres" pageTitle="Road Alert" />

          {/* En-tête de section */}
          <div className="d-flex align-items-center gap-2 mb-4">
            <div
              className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
              style={{ width: 48, height: 48, background: "rgba(255, 109, 0, 0.12)" }}
            >
              <i className="ri-settings-3-line fs-4 text-primary" />
            </div>
            <div>
              <h5 className="mb-0 fw-semibold">Paramètres</h5>
              <p className="text-muted small mb-0">Gravité, dégradations, régions, départements, etc.</p>
            </div>
          </div>

          <Card className="card-ageroute rounded-3 shadow-sm border-0 overflow-hidden">
            <CardBody className="p-0">
              <div className="px-3 pt-3 pb-0">
                <Nav tabs className="modern-tabs border-0">
                {TABS.map((tab) => (
                  <NavItem key={tab.id}>
                    <NavLink
                      className={classnames({ active: activeTab === tab.id })}
                      onClick={() => setActiveTab(tab.id)}
                      style={{ cursor: "pointer" }}
                    >
                      {tab.label}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>
              </div>
              <div className="px-3 pb-3 pt-2 mt-0">
                {canAddDeleteParametres && activeTab === "gravite" && (
                  <div className="mb-3 d-flex justify-content-end">
                    <Button
                      className="btn-ageroute-orange rounded-pill shadow-sm"
                      color="primary"
                      onClick={() => {
                        showModal({
                          title: "Ajouter une gravité",
                          size: "md",
                          content: <GraviteFormModal initialRow={null} onSuccess={handleParametresSuccess} />,
                          haveDefaultActionsButton: false,
                          closeOnBackdropClick: true,
                        });
                      }}
                    >
                      <i className="ri-add-line me-2" />
                      Ajouter une gravité
                    </Button>
                  </div>
                )}
                {canAddDeleteParametres && activeTab === "typeDegradation" && (
                  <div className="mb-3 d-flex justify-content-end">
                    <Button
                      className="btn-ageroute-orange rounded-pill shadow-sm"
                      color="primary"
                      onClick={() => {
                        showModal({
                          title: "Ajouter un type de dégradation",
                          size: "md",
                          content: <TypeDegradationFormModal initialRow={null} onSuccess={handleParametresSuccess} />,
                          haveDefaultActionsButton: false,
                          closeOnBackdropClick: true,
                        });
                      }}
                    >
                      <i className="ri-add-line me-2" />
                      Ajouter un type de dégradation
                    </Button>
                  </div>
                )}
                {canAddDeleteParametres && activeTab === "ensDegradation" && (
                  <div className="mb-3 d-flex justify-content-end">
                    <Button
                      className="btn-ageroute-orange rounded-pill shadow-sm"
                      color="primary"
                      onClick={() => {
                        showModal({
                          title: "Ajouter un ens. dégradation",
                          size: "md",
                          content: <EnsDegradationFormModal initialRow={null} onSuccess={handleParametresSuccess} />,
                          haveDefaultActionsButton: false,
                          closeOnBackdropClick: true,
                        });
                      }}
                    >
                      <i className="ri-add-line me-2" />
                      Ajouter un ens. dégradation
                    </Button>
                  </div>
                )}
                {canAddDeleteParametres && activeTab === "degradation" && (
                  <div className="mb-3 d-flex justify-content-end">
                    <Button
                      className="btn-ageroute-orange rounded-pill shadow-sm"
                      color="primary"
                      onClick={() => {
                        showModal({
                          title: "Ajouter une dégradation",
                          size: "md",
                          content: <DegradationFormModal initialRow={null} onSuccess={handleParametresSuccess} />,
                          haveDefaultActionsButton: false,
                          closeOnBackdropClick: true,
                        });
                      }}
                    >
                      <i className="ri-add-line me-2" />
                      Ajouter une dégradation
                    </Button>
                  </div>
                )}
                {canAddDeleteParametres && activeTab === "parametre" && (
                  <div className="mb-3 d-flex justify-content-end">
                    <Button
                      className="btn-ageroute-orange rounded-pill shadow-sm"
                      color="primary"
                      onClick={() => {
                        showModal({
                          title: "Ajouter un paramètre",
                          size: "md",
                          content: <ParametreFormModal initialRow={null} onSuccess={handleParametresSuccess} />,
                          haveDefaultActionsButton: false,
                          closeOnBackdropClick: true,
                        });
                      }}
                    >
                      <i className="ri-add-line me-2" />
                      Ajouter un paramètre
                    </Button>
                  </div>
                )}
                {canAddDeleteParametres && activeTab === "typeChausse" && (
                  <div className="mb-3 d-flex justify-content-end">
                    <Button
                      className="btn-ageroute-orange rounded-pill shadow-sm"
                      color="primary"
                      onClick={() => {
                        showModal({
                          title: "Ajouter un type chaussé",
                          size: "md",
                          content: <TypeChausseFormModal initialRow={null} onSuccess={handleParametresSuccess} />,
                          haveDefaultActionsButton: false,
                          closeOnBackdropClick: true,
                        });
                      }}
                    >
                      <i className="ri-add-line me-2" />
                      Ajouter un type chaussé
                    </Button>
                  </div>
                )}
                {canAddDeleteParametres && activeTab === "typeAlerte" && (
                  <div className="mb-3 d-flex justify-content-end">
                    <Button
                      className="btn-ageroute-orange rounded-pill shadow-sm"
                      color="primary"
                      onClick={() => {
                        showModal({
                          title: "Ajouter un type d'alerte",
                          size: "md",
                          content: <TypeAlerteFormModal initialRow={null} onSuccess={handleParametresSuccess} />,
                          haveDefaultActionsButton: false,
                          closeOnBackdropClick: true,
                        });
                      }}
                    >
                      <i className="ri-add-line me-2" />
                      Ajouter un type d'alerte
                    </Button>
                  </div>
                )}
                {canAddDeleteParametres && activeTab === "region" && (
                  <div className="mb-3 d-flex justify-content-end">
                    <Button
                      className="btn-ageroute-orange rounded-pill shadow-sm"
                      color="primary"
                      onClick={() => {
                        showModal({
                          title: "Ajouter une région",
                          size: "md",
                          content: <RegionFormModal initialRow={null} onSuccess={handleParametresSuccess} />,
                          haveDefaultActionsButton: false,
                          closeOnBackdropClick: true,
                        });
                      }}
                    >
                      <i className="ri-add-line me-2" />
                      Ajouter une région
                    </Button>
                  </div>
                )}
                {canAddDeleteParametres && activeTab === "departement" && (
                  <div className="mb-3 d-flex justify-content-end">
                    <Button
                      className="btn-ageroute-orange rounded-pill shadow-sm"
                      color="primary"
                      onClick={() => {
                        showModal({
                          title: "Ajouter un département",
                          size: "md",
                          content: <DepartementFormModal initialRow={null} onSuccess={handleParametresSuccess} />,
                          haveDefaultActionsButton: false,
                          closeOnBackdropClick: true,
                        });
                      }}
                    >
                      <i className="ri-add-line me-2" />
                      Ajouter un département
                    </Button>
                  </div>
                )}
                <DataTable
                  instanceId={`parametres-${activeTab}`}
                  columns={columns}
                  data={data}
                  isLoading={loading}
                  exportExcel={false}
                  exportPDF={false}
                  actions={filteredActions}
                  actionsColumnLabel="Actions"
                  emptyState={
                    <div className="text-center py-12">
                      <div className="text-gray-400 mb-4">
                        <i className="ri-database-line" style={{ fontSize: '4rem' }}></i>
                      </div>
                      <h3 className="text-lg fw-semibold text-gray-900 mb-2">
                        Aucune donnée disponible
                      </h3>
                      <p className="text-gray-500">
                        Il n'y a actuellement aucune donnée dans cette catégorie.
                      </p>
                    </div>
                  }
                  pageSize={10}
                  pageSizeOptions={[10, 20, 50, 100]}
                  searchable={true}
                  pagination={true}
                />
              </div>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Parametres;
